import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuthStore } from "../context/store.js";

const B = {
  bg: "#08070F", bg2: "#0D0C18", bg3: "#131124",
  violet: "#7C3AED", violetLt: "#8B5CF6",
  violetDim: "rgba(124,58,237,0.15)", violetBorder: "rgba(124,58,237,0.3)",
  green: "#10F5A0", coral: "#FF6B6B",
  text: "#F0EFFE", text2: "#7C7A9E", text3: "#3D3B5C",
  border: "rgba(124,58,237,0.15)", border2: "rgba(124,58,237,0.08)",
  grad: "linear-gradient(135deg, #7C3AED, #10F5A0)",
  font: "'Sora', system-ui, sans-serif",
  mono: "'Space Mono', monospace",
};

function VylappMark({ size = 48 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
      <defs>
        <linearGradient id="auth-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#10F5A0" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="12" fill="url(#auth-grad)" opacity="0.15" />
      <rect width="40" height="40" rx="12" fill="none" stroke="url(#auth-grad)" strokeWidth="1.5" opacity="0.5" />
      <path d="M10 11L20 29L30 11" stroke="url(#auth-grad)" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="20" cy="29" r="3" fill="url(#auth-grad)" />
    </svg>
  );
}

export default function AuthPage({ mode = "login" }) {
  const navigate = useNavigate();
  const { login, register, loading, error } = useAuthStore();
  const isLogin = mode === "login";

  const [form, setForm] = useState({ email: "", password: "", username: "", displayName: "" });
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    let result;
    if (isLogin) {
      result = await login(form.email, form.password);
    } else {
      result = await register(form.username, form.email, form.password, form.displayName);
    }
    if (result.ok) navigate("/");
  };

  const inputStyle = {
    width: "100%", background: B.bg3, border: `1px solid ${B.border}`,
    borderRadius: 12, padding: "13px 16px", color: B.text,
    fontFamily: B.font, fontSize: 15, outline: "none",
    transition: "border-color 0.2s",
  };

  return (
    <div style={{
      minHeight: "100vh", background: B.bg, display: "flex",
      fontFamily: B.font,
    }}>
      {/* ── Left panel — brand ── */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
        justifyContent: "center", padding: 48,
        background: `radial-gradient(ellipse at 30% 50%, rgba(124,58,237,0.12) 0%, transparent 70%)`,
      }}>
        <div style={{ maxWidth: 440, textAlign: "center" }}>
          <VylappMark size={72} />
          <div style={{
            fontSize: 52, fontWeight: 900, marginTop: 24,
            background: B.grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
            letterSpacing: "-2px", lineHeight: 1.1,
          }}>VYLAPP</div>
          <div style={{ color: B.text2, fontSize: 18, marginTop: 12, letterSpacing: 3, fontFamily: B.mono }}>
            VIBE · LEARN · CONNECT
          </div>
          <div style={{ color: B.text2, fontSize: 16, marginTop: 32, lineHeight: 1.8 }}>
            The global community platform where culture, discovery and real connection meet.
          </div>
          {/* Feature pills */}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 40 }}>
            {[
              ["⚡","Tech Vibes",    B.violet],
              ["🌍","Global Connect",B.green ],
              ["🎨","Creative Learn",B.coral ],
              ["🧠","Grow Together", "#A78BFA"],
            ].map(([e,l,c]) => (
              <span key={l} style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "6px 14px", borderRadius: 9999,
                background: `${c}12`, color: c, fontSize: 13, fontWeight: 600,
                border: `1px solid ${c}25`,
              }}>{e} {l}</span>
            ))}
          </div>
          {/* Stats */}
          <div style={{ display: "flex", gap: 40, justifyContent: "center", marginTop: 48 }}>
            {[["47","Countries"],["12k+","Members"],["365","Days/year"]].map(([n,l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 28, fontWeight: 900, background: B.grad, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>{n}</div>
                <div style={{ fontSize: 12, color: B.text2, marginTop: 2, fontFamily: B.mono, letterSpacing: 1 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right panel — auth form ── */}
      <div style={{
        width: "100%", maxWidth: 480, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: 32, borderLeft: `1px solid ${B.border2}`,
      }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          {/* Mobile logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 36 }}>
            <VylappMark size={36} />
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, background: B.grad, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" }}>VYLAPP</div>
              <div style={{ fontSize: 10, color: B.text2, fontFamily: B.mono, letterSpacing: 1.5 }}>VIBE · LEARN · CONNECT</div>
            </div>
          </div>

          <h1 style={{ fontSize: 28, fontWeight: 900, color: B.text, marginBottom: 6 }}>
            {isLogin ? "Welcome back" : "Join Vylapp"}
          </h1>
          <p style={{ color: B.text2, fontSize: 15, marginBottom: 32 }}>
            {isLogin ? "Sign in to vibe with your community." : "Create your account and start vibing."}
          </p>

          {error && (
            <div style={{ background: "rgba(255,107,107,0.12)", border: "1px solid rgba(255,107,107,0.3)", borderRadius: 10, padding: "12px 16px", color: B.coral, fontSize: 14, marginBottom: 20 }}>
              {error}
            </div>
          )}

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {!isLogin && (
              <>
                <input style={inputStyle} placeholder="Display name" value={form.displayName} onChange={set("displayName")} required
                  onFocus={e => e.target.style.borderColor = B.violetBorder}
                  onBlur={e => e.target.style.borderColor = B.border} />
                <input style={inputStyle} placeholder="Username (letters, numbers, _)" value={form.username} onChange={set("username")} required pattern="[a-zA-Z0-9_]+"
                  onFocus={e => e.target.style.borderColor = B.violetBorder}
                  onBlur={e => e.target.style.borderColor = B.border} />
              </>
            )}
            <input style={inputStyle} type={isLogin ? "text" : "email"} placeholder={isLogin ? "Email or username" : "Email address"} value={form.email} onChange={set("email")} required
              onFocus={e => e.target.style.borderColor = B.violetBorder}
              onBlur={e => e.target.style.borderColor = B.border} />
            <input style={inputStyle} type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} required minLength={6}
              onFocus={e => e.target.style.borderColor = B.violetBorder}
              onBlur={e => e.target.style.borderColor = B.border} />

            <button type="submit" disabled={loading} style={{
              width: "100%", padding: "14px", borderRadius: 12, border: "none",
              background: loading ? `${B.violet}60` : B.grad,
              color: "#fff", fontFamily: B.font, fontWeight: 700, fontSize: 16,
              cursor: loading ? "default" : "pointer",
              boxShadow: loading ? "none" : "0 4px 24px rgba(124,58,237,0.4)",
              transition: "all 0.2s", marginTop: 6,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}>
              {loading ? <><div className="vyl-spinner" /> Loading…</> : isLogin ? "Sign In ✦" : "Create Account ✦"}
            </button>
          </form>

          {/* Demo credentials */}
          {isLogin && (
            <div style={{ marginTop: 20, padding: "12px 16px", background: B.bg3, borderRadius: 10, border: `1px solid ${B.border2}` }}>
              <div style={{ color: B.text2, fontSize: 12, fontFamily: B.mono, marginBottom: 6 }}>DEMO CREDENTIALS</div>
              <div style={{ color: B.green, fontSize: 13 }}>you@vylapp.com / vylapp123</div>
            </div>
          )}

          <div style={{ textAlign: "center", marginTop: 24, color: B.text2, fontSize: 14 }}>
            {isLogin ? (
              <>Don't have an account? <Link to="/register" style={{ color: B.violetLt, fontWeight: 700, textDecoration: "none" }}>Join Vylapp</Link></>
            ) : (
              <>Already a viber? <Link to="/login" style={{ color: B.violetLt, fontWeight: 700, textDecoration: "none" }}>Sign in</Link></>
            )}
          </div>

          <div style={{ textAlign: "center", marginTop: 32, color: B.text3, fontSize: 12 }}>
            By joining, you agree to our{" "}
            <span style={{ color: B.violetLt, cursor: "pointer" }}>Terms</span> &{" "}
            <span style={{ color: B.violetLt, cursor: "pointer" }}>Privacy Policy</span>
          </div>
        </div>
      </div>
    </div>
  );
}
